import { useState, useMemo, useEffect } from "react"
import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import type { SeoMeta } from "@/types"
import {
  DEFAULT_CHANNELS,
  calculateMarketingRoi,
  formatCurrency,
} from "@/data/tools/marketing-roi-defaults"
import type { MarketingChannel } from "@/data/tools/marketing-roi-defaults"
import { CalculatorResultCard, CalculatorActionsBar } from "@/components/public/calculators"
import { generateCalculatorPdf } from "@/lib/calculator-pdf"

/* ------------------------------------------------------------------ */
/* FAQ data                                                            */
/* ------------------------------------------------------------------ */

const FAQ = [
  {
    q: "How do I calculate marketing ROI for my agency?",
    a: "Track your annual spend per channel (portals, photography, video, social media, print, signage) and the number of quality leads each channel generates. Divide the spend by leads to get cost-per-lead. The channel with the lowest cost-per-lead delivers the best return on investment.",
  },
  {
    q: "What is a good cost-per-lead for estate agents?",
    a: "Cost-per-lead varies hugely by channel and location. Portal leads typically cost £100-200 each. Social media leads can be as low as £30-80. Video-generated leads often fall in the £40-60 range because video content drives higher engagement and sharing. The key is tracking which leads convert to instructions, not just raw lead volume.",
  },
  {
    q: "Why does video have a good ROI?",
    a: "Video combines broad reach with high engagement. A property video shared on social media can generate views, shares, and enquiries at a fraction of the cost of traditional advertising. It also differentiates your agency at market appraisals, helping you win instructions, the true measure of marketing ROI.",
  },
  {
    q: "Should I stop spending on portals?",
    a: "No. Portals remain essential for reach; most buyers start their search on Rightmove or Zoopla. The goal is to optimise your marketing mix: maintain portal presence but supplement it with lower-cost-per-lead channels like video and social media that also build your brand.",
  },
  {
    q: "How much should estate agents spend on marketing?",
    a: "Most successful agencies spend between 5% and 10% of their gross revenue on marketing. For an agency turning over £500,000 a year, that means £25,000-50,000 across all channels. The key is not the total spend but how effectively it converts into instructions. Track your cost-per-instruction, not just cost-per-lead.",
  },
  {
    q: "What is the difference between a lead and an instruction?",
    a: "A lead is an initial enquiry or expression of interest, such as a phone call, email, or form submission. An instruction is when a vendor formally appoints your agency to sell their property. Tracking leads is useful, but instructions are what generate revenue. The best marketing channels deliver leads that convert into instructions, not just high volumes of unqualified enquiries.",
  },
  {
    q: "How do I track which marketing channel generates leads?",
    a: "Use a combination of methods: unique phone numbers for different channels, UTM parameters on web links, a simple 'how did you hear about us?' question at first contact, and your CRM's source tracking. Even a basic spreadsheet updated weekly gives you enough data to compare channel performance after a few months.",
  },
  {
    q: "Is social media marketing worth it for estate agents?",
    a: "Yes, when done well. Social media builds local brand awareness and generates vendor leads at a lower cost than most traditional channels. Property videos perform particularly well on Facebook and Instagram, where they can be targeted to homeowners in specific postcodes. The combination of low cost-per-lead and strong brand-building makes social media one of the highest-ROI channels available.",
  },
  {
    q: "How long before I see ROI from video marketing?",
    a: "Most agents see results within the first month. Video content starts generating views and engagement immediately on social media, and the impact at market appraisals is felt from your very first video-equipped valuation. The compounding effect of a growing library of video content means ROI typically improves over time as your back catalogue continues to attract attention.",
  },
  {
    q: "Should I invest in photography or video first?",
    a: "Photography comes first as it is essential for portal listings and forms the foundation of your marketing. However, if you already have good photography in place, video is the highest-impact addition you can make. With QuiteRight, you can create videos from your existing photographs, so you do not need to choose between the two.",
  },
]

/* ------------------------------------------------------------------ */
/* Channel row                                                         */
/* ------------------------------------------------------------------ */

function ChannelRow({
  channel,
  onChange,
}: {
  channel: MarketingChannel
  onChange: (updated: MarketingChannel) => void
}) {
  return (
    <div className="grid grid-cols-[1fr_100px_80px] items-center gap-3 sm:grid-cols-[1fr_120px_100px]">
      <span className="text-sm font-medium text-charcoal">{channel.name}</span>
      <div className="relative">
        <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-warm-grey">
          £
        </span>
        <input
          type="text"
          inputMode="numeric"
          value={channel.annualCost.toLocaleString("en-GB")}
          onChange={(e) => {
            const raw = e.target.value.replace(/[^0-9]/g, "")
            const num = parseInt(raw, 10)
            if (!isNaN(num)) onChange({ ...channel, annualCost: num })
            if (raw === "") onChange({ ...channel, annualCost: 0 })
          }}
          className="w-full rounded-lg border border-stone bg-white py-2 pl-6 pr-2 text-right text-sm text-charcoal focus:border-red/40 focus:outline-none focus:ring-1 focus:ring-red/20"
        />
      </div>
      <input
        type="text"
        inputMode="numeric"
        value={channel.leadsPerYear}
        onChange={(e) => {
          const raw = e.target.value.replace(/[^0-9]/g, "")
          const num = parseInt(raw, 10)
          if (!isNaN(num)) onChange({ ...channel, leadsPerYear: num })
          if (raw === "") onChange({ ...channel, leadsPerYear: 0 })
        }}
        className="w-full rounded-lg border border-stone bg-white py-2 px-2 text-right text-sm text-charcoal focus:border-red/40 focus:outline-none focus:ring-1 focus:ring-red/20"
      />
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* ROI bar chart                                                       */
/* ------------------------------------------------------------------ */

function RoiBarChart({
  channels,
}: {
  channels: { id: string; name: string; costPerLead: number }[]
}) {
  const validChannels = channels.filter((ch) => ch.costPerLead > 0)
  if (validChannels.length === 0) return null

  const maxCpl = Math.max(...validChannels.map((ch) => ch.costPerLead))

  return (
    <div className="rounded-xl border border-stone bg-white p-5">
      <p className="text-xs font-semibold uppercase tracking-[0.15em] text-charcoal">
        Cost per lead by channel (lower is better)
      </p>
      <div className="mt-4 space-y-3">
        {validChannels.map((ch) => {
          const widthPercent = maxCpl > 0 ? (ch.costPerLead / maxCpl) * 100 : 0
          const isVideo = ch.id === "video"
          return (
            <div key={ch.id}>
              <div className="flex items-baseline justify-between text-xs">
                <span className={`font-medium ${isVideo ? "text-red" : "text-charcoal"}`}>
                  {ch.name}
                </span>
                <span className="font-semibold text-charcoal">
                  {formatCurrency(Math.round(ch.costPerLead))}
                </span>
              </div>
              <div className="mt-1 h-3 overflow-hidden rounded-full bg-stone">
                <div
                  className={`h-full rounded-full transition-all duration-500 ${
                    isVideo ? "bg-red" : "bg-charcoal/30"
                  }`}
                  style={{ width: `${Math.max(widthPercent, 3)}%` }}
                />
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* Page component                                                      */
/* ------------------------------------------------------------------ */

export default function MarketingRoiCalculatorPage({
  meta,
}: {
  meta: SeoMeta
}) {
  const initialChannels = useMemo(() => {
    if (typeof window === "undefined") return DEFAULT_CHANNELS.map((ch) => ({ ...ch }))
    const sp = new URLSearchParams(window.location.search)
    return DEFAULT_CHANNELS.map((ch) => {
      const cost = sp.get(`${ch.id}_cost`)
      const leads = sp.get(`${ch.id}_leads`)
      return {
        ...ch,
        annualCost: cost !== null ? parseInt(cost, 10) || ch.annualCost : ch.annualCost,
        leadsPerYear: leads !== null ? parseInt(leads, 10) || ch.leadsPerYear : ch.leadsPerYear,
      }
    })
  }, [])

  const [channels, setChannels] = useState<MarketingChannel[]>(initialChannels)

  const updateChannel = (index: number, updated: MarketingChannel) => {
    setChannels((prev) => prev.map((ch, i) => (i === index ? updated : ch)))
  }

  const result = calculateMarketingRoi(channels)

  useEffect(() => {
    const params = new URLSearchParams()
    for (const ch of channels) {
      params.set(`${ch.id}_cost`, String(ch.annualCost))
      params.set(`${ch.id}_leads`, String(ch.leadsPerYear))
    }
    window.history.replaceState(null, "", `?${params.toString()}`)
  }, [channels])

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}`
      : ""
  const shareTitle = `Marketing ROI: ${formatCurrency(result.avgCostPerLead)} average cost per lead across ${channels.length} channels`

  const handleDownloadPdf = () =>
    generateCalculatorPdf({
      title: "Marketing ROI Analysis",
      subtitle: `Cost-per-lead comparison across ${channels.length} marketing channels`,
      sections: [
        {
          title: "Summary",
          rows: [
            { label: "Total annual spend", value: formatCurrency(result.totalSpend), sub: `Across ${channels.length} channels` },
            { label: "Total leads per year", value: `${result.totalLeads}`, sub: "All channels combined" },
            { label: "Average cost per lead", value: formatCurrency(result.avgCostPerLead), sub: "Blended across all channels", accent: true },
          ],
        },
        {
          title: "Cost per Lead by Channel",
          rows: result.channels
            .filter((ch) => ch.costPerLead > 0)
            .map((ch) => ({
              label: ch.name,
              value: formatCurrency(Math.round(ch.costPerLead)),
              sub: `${formatCurrency(ch.annualCost)} spend, ${ch.leadsPerYear} leads`,
            })),
        },
      ],
      url: shareUrl,
      generatedAt: new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }),
    })

  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />
        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            name: "Marketing ROI Calculator for Estate Agents",
            description: meta.description,
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            offers: { "@type": "Offer", price: "0", priceCurrency: "GBP" },
            provider: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://quiteright.co.uk",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>
        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "Tools", path: "/tools" }, { name: "Marketing ROI Calculator" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-4xl text-center">
          <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
            Calculator
          </p>
          <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl md:text-6xl">
            Marketing ROI calculator
            <span className="text-red">.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-slate">
            Compare cost-per-lead across your estate agency marketing channels.
            See which investments deliver the best return.
          </p>
        </div>
      </section>

      {/* Calculator */}
      <section className="border-t border-stone bg-white px-6 py-16">
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-16">
            {/* Input panel */}
            <div className="flex-1 space-y-6">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Your marketing spend<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Enter your annual cost and leads generated per channel.
                </p>
              </div>

              {/* Column headers */}
              <div className="grid grid-cols-[1fr_100px_80px] items-end gap-3 sm:grid-cols-[1fr_120px_100px]">
                <span className="text-xs font-medium uppercase tracking-wider text-warm-grey">
                  Channel
                </span>
                <span className="text-right text-xs font-medium uppercase tracking-wider text-warm-grey">
                  Annual cost
                </span>
                <span className="text-right text-xs font-medium uppercase tracking-wider text-warm-grey">
                  Leads/yr
                </span>
              </div>

              <div className="space-y-3">
                {channels.map((channel, index) => (
                  <ChannelRow
                    key={channel.id}
                    channel={channel}
                    onChange={(updated) => updateChannel(index, updated)}
                  />
                ))}
              </div>
            </div>

            {/* Results panel */}
            <div className="flex-1 space-y-4">
              <div>
                <h2 className="font-display text-xl font-bold text-charcoal">
                  Your ROI<span className="text-red">.</span>
                </h2>
                <p className="mt-1 text-sm text-slate">
                  Ranked by cost-per-lead, lowest is best.
                </p>
              </div>

              <CalculatorActionsBar shareUrl={shareUrl} shareTitle={shareTitle} onDownloadPdf={handleDownloadPdf} />

              <div className="grid grid-cols-2 gap-4">
                <CalculatorResultCard
                  label="Total annual spend"
                  value={formatCurrency(result.totalSpend)}
                  sub={`Across ${channels.length} channels`}
                />
                <CalculatorResultCard
                  label="Total leads per year"
                  value={`${result.totalLeads}`}
                  sub="All channels combined"
                />
                <CalculatorResultCard
                  label="Average cost per lead"
                  value={formatCurrency(result.avgCostPerLead)}
                  sub="Blended across all channels"
                  accent
                  large
                />
                {result.channels.length > 0 && result.channels[0].costPerLead > 0 && (
                  <CalculatorResultCard
                    label="Most efficient channel"
                    value={result.channels[0].name}
                    sub={`${formatCurrency(Math.round(result.channels[0].costPerLead))} per lead`}
                  />
                )}
              </div>

              <RoiBarChart channels={result.channels} />
            </div>
          </div>
        </div>
      </section>

      {/* Methodology */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            How to measure marketing ROI<span className="text-red">.</span>
          </h2>
          <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate">
            <p>
              The simplest measure is <strong className="text-charcoal">cost per lead</strong>:
              divide your annual spend on each channel by the number of
              quality leads it generates. A &ldquo;lead&rdquo; should be a
              genuine enquiry from a potential vendor or buyer, not just a
              click or impression.
            </p>
            <p>
              For a more complete picture, track leads through to instructions
              and completions. A channel with a higher cost-per-lead but
              better conversion to instructions may still deliver a superior
              return.
            </p>
            <p>
              The default figures are based on typical UK agency budgets.
              Adjust them to match your own spend and results for an accurate
              comparison.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-t border-stone bg-charcoal px-6 py-16">
        <ScrollReveal
          stagger
          className="mx-auto grid max-w-5xl grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate/20 bg-slate/20 sm:grid-cols-4"
        >
          {[
            { stat: "5x", label: "more engagement from video vs static photos" },
            { stat: "73%", label: "of vendors prefer agents offering video" },
            { stat: "£45", label: "typical cost per lead from video marketing" },
            { stat: "<60s", label: "to create a listing video with QuiteRight" },
          ].map((item) => (
            <div key={item.stat} className="bg-charcoal px-5 py-6 text-center">
              <p className="font-display text-2xl font-bold text-cream md:text-3xl">
                {item.stat}
              </p>
              <p className="mt-2 text-xs leading-snug text-warm-grey">{item.label}</p>
            </div>
          ))}
        </ScrollReveal>
      </section>

      {/* Related tools */}
      <section className="border-t border-stone px-6 py-16">
        <div className="mx-auto max-w-3xl">
          <h2 className="font-display text-2xl font-bold text-charcoal">
            Related tools<span className="text-red">.</span>
          </h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2">
            <Link
              href="/tools/commission-calculator"
              className="group rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/20 hover:shadow-sm"
            >
              <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                Commission calculator
              </p>
              <p className="mt-1 text-sm text-slate">
                Calculate your commission and what a fee increase could mean.
              </p>
            </Link>
            <Link
              href="/tools/listing-cost-calculator"
              className="group rounded-xl border border-stone bg-white p-5 transition-all hover:border-red/20 hover:shadow-sm"
            >
              <p className="font-display text-base font-bold text-charcoal transition-colors group-hover:text-red">
                Listing cost calculator
              </p>
              <p className="mt-1 text-sm text-slate">
                Compare the cost of marketing each listing.
              </p>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal className="text-center">
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions<span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 divide-y divide-stone">
            {FAQ.map((item) => (
              <details key={item.q} className="group py-5">
                <summary className="flex cursor-pointer items-center justify-between text-base font-medium text-charcoal">
                  {item.q}
                  <svg
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    className="size-5 shrink-0 text-warm-grey transition-transform group-open:rotate-180"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z"
                      clipRule="evenodd"
                    />
                  </svg>
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-stone bg-charcoal px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <h2 className="font-display text-3xl font-bold text-cream md:text-4xl lg:text-5xl">
            Video delivers the best ROI per lead
            <span className="text-red">.</span>
          </h2>
          <p className="mx-auto mt-6 max-w-xl text-lg text-warm-grey">
            Start creating scroll-stopping property videos for less than the
            cost of a single portal listing upgrade. See the difference video
            makes to your marketing mix.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md bg-red px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-deep-red sm:w-auto"
            >
              Request a demo
            </Link>
            <Link
              href="/demo"
              className="inline-flex w-full items-center justify-center rounded-md border border-slate bg-charcoal px-8 py-4 text-lg font-medium text-cream transition-colors hover:bg-slate sm:w-auto"
            >
              Book a demo
            </Link>
          </div>
          <p className="mt-5 text-sm text-warm-grey">
            14-day free trial &middot; No credit card &middot; Cancel any time
          </p>
        </div>
      </section>
    </PublicLayout>
  )
}
